/**
 * Google OAuth Provider - Sprint 1b
 *
 * Implements OAuth 2.0 flow for Google Gmail API access.
 * Supports refresh tokens with offline access.
 *
 * @see https://developers.google.com/identity/protocols/oauth2/web-server
 * @see docs/sprints/ownyou-sprint1b-spec.md
 */
/**
 * Google OAuth endpoints
 */
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';
const GOOGLE_REVOKE_URL = 'https://oauth2.googleapis.com/revoke';
/**
 * Google OAuth Provider implementation
 */
export class GoogleOAuthProvider {
    provider = 'google';
    config;
    constructor(config) {
        this.config = config;
    }
    /**
     * Generate authorization URL for Google OAuth
     *
     * @param state - Random state string for CSRF protection
     * @returns Authorization URL to redirect user to
     */
    getAuthorizationUrl(state) {
        const params = new URLSearchParams({
            client_id: this.config.clientId,
            response_type: 'code',
            redirect_uri: this.config.redirectUri,
            scope: this.config.scopes.join(' '),
            state,
            access_type: 'offline', // Required for refresh token
            prompt: 'consent', // Force consent to ensure refresh token is returned
        });
        return `${GOOGLE_AUTH_URL}?${params.toString()}`;
    }
    /**
     * Exchange authorization code for tokens
     *
     * Uses public-client token exchange with Google's OAuth endpoint.
     * Browser builds must not include provider client secrets.
     *
     * @param code - Authorization code from callback
     * @returns Stored tokens with access, refresh, and expiry
     */
    async exchangeCode(code, codeVerifier) {
        // Build token exchange parameters
        const bodyParams = {
            client_id: this.config.clientId,
            grant_type: 'authorization_code',
            code,
            redirect_uri: this.config.redirectUri,
        };
        if (codeVerifier) {
            bodyParams.code_verifier = codeVerifier;
        }
        const body = new URLSearchParams(bodyParams);
        const response = await fetch(GOOGLE_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: body.toString(),
        });
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Google token exchange failed: ${error}`);
        }
        const data = await response.json();
        return this.parseTokenResponse(data);
    }
    /**
     * Refresh expired tokens
     *
     * @param refreshToken - Valid refresh token
     * @returns New tokens with updated access token and expiry
     */
    async refreshTokens(refreshToken) {
        const bodyParams = {
            client_id: this.config.clientId,
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        };
        const body = new URLSearchParams(bodyParams);
        const response = await fetch(GOOGLE_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: body.toString(),
        });
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Google token refresh failed: ${error}`);
        }
        const data = await response.json();
        // Google doesn't return a new refresh token on refresh
        // Keep the existing refresh token
        return {
            ...this.parseTokenResponse(data),
            refreshToken,
        };
    }
    /**
     * Revoke tokens
     *
     * @param tokens - Tokens to revoke
     */
    async revokeTokens(tokens) {
        const response = await fetch(`${GOOGLE_REVOKE_URL}?token=${tokens.accessToken}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        if (!response.ok) {
            // Revocation may fail if token is already expired/invalid
            // This is not critical, just log and continue
            console.warn('Google token revocation failed:', await response.text());
        }
    }
    /**
     * Parse Google token response into StoredTokens format
     */
    parseTokenResponse(data) {
        const expiresIn = data.expires_in ?? 3600;
        return {
            accessToken: data.access_token,
            refreshToken: data.refresh_token ?? '',
            expiresAt: Date.now() + expiresIn * 1000,
            scope: data.scope ?? this.config.scopes.join(' '),
            tokenType: data.token_type ?? 'Bearer',
        };
    }
}
//# sourceMappingURL=google.js.map